import * as angular from 'angular';

import { Inject, Injectable, Optional, SkipSelf } from "@angular/core";
import { downgradeInjectable } from "@angular/upgrade/static";

import { HttpClient } from "@angular/common/http";
import { Observable } from 'rxjs';

import { HttpService } from '../../../../shared/ajs-bridges/http.service';
import { BasicDataSubService } from '../../../../shared/basic-data-management/basic-data-subservice.class';

import { CachedDataSurveyService } from '../../../../technical-modules/tasks/services/cached-data-survey.service';

import { UserDTO } from '../entities/user-dto.entity';
import { UserTLR } from '../entities/user-tlr.entity';
import { LabelValue } from '../../../../shared/suggestable-input/entities/label-value.entity';
import { UserPreferenceNotification } from '../entities/user-preference-notification';
import { UserPreferenceNotificationBase } from '../entities/user-preference-notification-base';

import { TypeResponsabilite } from '../../../perimetres/entities/type-responsabilite.entity';
import { AntenneTLR } from '../../../structure/antenne/entities/antenne-tlr.entity';
import { ParcTLR } from '../../../structure/parc/entities/parc-tlr.entity';
import { OuvrageTLR } from '../../../structure/ouvrage/entities/ouvrage-tlr.entity';

@Injectable({ providedIn: 'root' })
export class UserService {
	private path: string;

	private userTLRSubService!: BasicDataSubService<UserTLR>;

	constructor( //
		@Inject(HttpClient) private httpClient: HttpClient, //
		@Inject(HttpService) private httpService: HttpService, //
		@Inject(CachedDataSurveyService) cachedDataSurveyService: CachedDataSurveyService, //
		@Optional() @SkipSelf() previousInstance?: UserService //
	) {
		if (previousInstance) {
			throw new Error(
				'This service is already loaded. Import it in the AppModule only');
		}
		this.path = (window as any).baseApiPath;
		let usersChanged$ = cachedDataSurveyService.getChangeNotification("User");
		this.userTLRSubService = new BasicDataSubService<UserTLR>(httpClient, this.path + 'users?format=TLR', usersChanged$);
	}

	public doLogin(identifiant: string): Promise<any> {
		this.currentUserTLRCache = null;
		let url = this.path + 'users/login/' + identifiant;
		return this.httpService.get(url, { cache: false });
	}

	public doLogout(identifiant: string): Promise<any> {
		this.currentUserTLRCache = null;
		let url = this.path + 'users/logout/' + identifiant;
		return this.httpService.get(url, { cache: false });
	}

	public logout(): Promise<any> {
		let url = this.path + 'users/logout/';
		return this.httpService.get(url, { cache: false });
	}

	/* Tous les utilisateurs */
	public getUsers(): Promise<any> {
		let url = this.path + 'users/';
		let result = this.httpService.get(url);
		return result;
	}

	/* Tous les CAC */
	public getAllCac(): Promise<any> {
		let url = this.path + 'users?reponsabilite=CAC';
		let result = this.httpService.get(url);
		return result;
	}

	// Methode recuperant les ouvrages selon le profil et l'antenne de l'utilisateur.
	public getAllUsersSelonProfilAndAntenne(user: UserDTO): Promise<any> {
		let url = this.path + 'users/';
		if (user.profil == 'REFERENT' && user.role == 'EXP') {
			url = this.path + 'users/antenne/' + user.idAntenne;
		}
		let result = this.httpService.get(url);
		return result;
	}

	// Methode permettant de recuperer un user à partir de son id.
	public getUserById(id: number) {
		let url = this.path + 'users/' + id;
		let result = this.httpService.get(url);
		return result;
	}

	//Methode permettant de créer un user
	public create(data: any): Promise<any> {
		let url = this.path + 'users/';
		let result = this.httpService.post(url, data);
		return result;
	}

	/* Methode permettant de  Supprimer un user à partir de son id  */
	public deleteUser(id: number): Promise<any> {
		let url = this.path + 'users/' + id;
		let result = this.httpService.delete(url);
		return result;
	}

	/*  modifier un user à partir de son id  */
	public update(id: number, data: any): Promise<any> {
		let url = this.path + 'users/' + id;
		let result = this.httpService.put(url, data);
		return result;
	}

	// Methode permettant de récupérer tous les utilisateurs de l'antenne {idAntenne}
	public getAllUsersByAntenne(idAntenne: number): Promise<any> {
		let url = this.path + 'users/antenne/' + idAntenne;
		let result = this.httpService.get(url);
		return result;
	}

	// Methode recuperant toutes les antennes.
	public getAntennes(): Promise<any> {
		let url = this.path + 'antennes/';
		let result = this.httpService.get(url);
		return result;
	}
	
	public getCurrentUser(): Promise<any> {
		let url = this.path + 'users?onlyCurrent=true';
		let result = this.httpService.get(url);
		return result;
	}
	
	///////////////////////////////////////////
	// Nouvelles fonctions en pur Angular 12 //
	///////////////////////////////////////////

	// Methode permettant le chargement de tous les users
	public getUserTLRList = () => this.userTLRSubService.getList();

	// Methodes permettant le chargement de tous les users, selon differentes classifications et indexs
	// (NB: on procede a toutes les classifications en une fois, ce qui permet de n'iterer qu'une seule fois pour tous les resultats)
	public getUserTLRMapById = () => this.userTLRSubService.getMapById();
	public getUserTLRById = (id: number) => this.userTLRSubService.getKnownById(id, "UserTLR");
	
	// Methode permettant le chargement de l'utilisateur connecte
	private currentUserTLRCache: Observable<UserTLR> | null = null;
	public getCurrentUserTLR(): Observable<UserTLR> {
		if (!this.currentUserTLRCache) {
			let url = this.path + 'users?onlyCurrent=true&format=TLR';
			this.currentUserTLRCache = this.httpClient.get<UserTLR>(url);
		}

		return this.currentUserTLRCache;
	}

	// Methode permettant de recuperer tous les CAC sous format TLR
	public getAllResponsablesTLR(responsabilite: TypeResponsabilite): Observable<UserTLR[]> {
		let url = this.path + 'users?format=TLR&responsabilite=' + responsabilite;
		return this.httpClient.get<UserTLR[]>(url);
	}
	
	// Methode permettant de recuperer tous les valideurs potentiels
	public getPotentialValidators(): Observable<LabelValue<number>[]> {
		let url = this.path + 'users/potentialValidators';
		return this.httpClient.get<LabelValue<number>[]>(url);
	}
	
	// Methode permettant de recuperer 
	public getPreferencesNotifications(userId: number): Observable<UserPreferenceNotification[]> {
		let url = this.path + 'users/' + userId + '/preferences-notifications';
		return this.httpClient.get<UserPreferenceNotification[]>(url);
	}

	// Methode de mise a jour des preferences de notifications d'un utilisateur
	public updatePreferencesNotifications(userId: number, command: UserPreferenceNotificationBase[]): Observable<void> {
		let url = this.path + 'users/' + userId + '/update-preferences-notifications-order';
		let result = this.httpClient.post<void>(url, command);
		return result;
	}
	
	/* Indique si l'utilisateur est CAC sur au moins un ouvrage */
	public isUserCacOnAtLeastOneOuvrage(userId: number): Observable<boolean> {
		let url = this.path + 'users/isUserCacOnAtLeastOneOuvrage/' + userId;
		let result = this.httpClient.get<boolean>(url);
		return result;
	}

	/* Indique si l'utilisateur est CAC sur au moins un ouvrage */
	public isUserCcOnAtLeastOneOuvrage(userId: number): Observable<boolean> {
		let url = this.path + 'users/isUserCcOnAtLeastOneOuvrage/' + userId;
		let result = this.httpClient.get<boolean>(url);
		return result;
	}

	public listActiveCACTLRByAntennes(antenneIds: number[]): Observable<UserTLR[]> {
		let url = this.path + 'users/habilites-cac';
		 url += JSON.stringify(antenneIds).replace('[', '?antenneIds=').replace(']', '').replace(/\,/g, '&antenneIds=');
		return this.httpClient.get<UserTLR[]>(url);
	}

	public listActiveCCTLRByAntennes(antenneIds: number[]): Observable<UserTLR[]> {
		let url = this.path + 'users/habilites-cc';
		url += JSON.stringify(antenneIds).replace('[', '?antenneIds=').replace(']', '').replace(/\,/g, '&antenneIds=');
		return this.httpClient.get<UserTLR[]>(url);
	}

	public getAntennesOnWhichUserIsCC(): Observable<AntenneTLR[]> {
		let url = this.path + 'users/antennesOnWhichUserIsCC';
		let result = this.httpClient.get<AntenneTLR[]>(url);
		return result;
	}

	public getParcsOnWhichUserIsCC(): Observable<ParcTLR[]> {
		let url = this.path + 'users/parcsOnWhichUserIsCC';
		let result = this.httpClient.get<ParcTLR[]>(url);
		return result;
	}

	public getOuvragesOnWhichUserIsCC(): Observable<OuvrageTLR[]> {
		let url = this.path + 'users/ouvragesOnWhichUserIsCC';
		let result = this.httpClient.get<OuvrageTLR[]>(url);
		return result;
	}
}

angular
	.module('auroraApp')
	.service('userService', downgradeInjectable(UserService));